import { useCallback, useMemo, useState } from "react"
import {
  Box,
  Stepper,
  Typography,
  FormControlLabel,
  Paper,
  Switch,
  useTheme,
  Backdrop,
  CircularProgress,
  Slide,
  Fab,
  Avatar,
} from "@mui/material"
import _ from "lodash"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { Trans } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useDirectionOverview } from "hooks/direction/useDirectionOverview"
import { useDirectionOverviewMap } from "hooks/direction/useDirectionOverviewMap"
import { useAppUI } from "providers/ui"

import {
  DirectionStepItem,
  DirectionCardActions,
} from "components/domains/direction"
import { DestinationMarkerIcon } from "components/icons"
import { BorderLinearProgress } from "components/Progress/BorderLinearProgress"

import { DirectionStepIcon } from "extensions/one-siam/components/domains/direction"
import { DirectionSearchContainer } from "containers/DirectionSearchContainer"
import { OrdinalSelector } from "components/OrdinalSelector"

export const DirectionOverviewPage = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { isMultiOrdinalVenue } = useVenue()

  const {
    isCalculatingRoute,
    steps,
    duration,
    distance,
    currentOrdinal,
    originData,
    originName,
    originLogo,
    originCategoryIconPath,
    destinationData,
    destinationName,
    destinationLogo,
    destinationCategoryIconPath,
    // destinationLevelName,
    elevatorPreferredMode,
    relatedLevelWithSteps,
    scannedLocations,
    userLocation,
    route,
    changeOrdinal,
    toggleElevatorPreference,
    startNavigate,
  } = useDirectionOverview()

  const stepsAvailable = steps && steps.length > 0

  useDirectionOverviewMap(route, currentOrdinal)

  const { t } = useTranslation()

  const { goBack } = useAppUI()

  const theme = useTheme()

  const { paper, formControlLabel } =
    theme.components[`AppDirectionSearch`]?.styleOverrides || {}
  const {
    root,
    // Direction Overview
    actionButtonWrapper,
    container,
    backButton,
    cardContainer,
    cardBody,
    cardAction,
    headerWrapper,
    locationHeaderWrapper,
    locationContainer,
    primaryText,
    secondaryText,
    durationText,
    // Step Overview
    stepCardContainer,
    stepper,
    stepCardBody,
    originStep,
    destinationStep,
    stepIconContainer,
    stepAvatar,
  } = theme.components[`AppDirectionOverview`]?.styleOverrides || {}

  const {
    overviewProgressContainer,
    originIconContainer,
    destinationIconContainer,
    locationAvatar,
  } = theme.components[`AppNavigationStep`]?.styleOverrides || {}

  const { originIcon, destinationIcon, highlightedText } =
    theme.components[`DirectionStep`]?.styleOverrides || {}

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <ArrowBackIcon />,
    [theme]
  )

  const originIconComp = useMemo(() => {
    const icon = _.get(
      theme,
      "assets.navigation.origin",
      originCategoryIconPath
    )
    return (
      getIcon(icon, originIcon) || <DestinationMarkerIcon sx={originIcon} />
    )
  }, [theme, originIcon, originCategoryIconPath])

  const destinationIconComp = useMemo(() => {
    const icon = _.get(
      theme,
      "assets.navigation.destination",
      destinationCategoryIconPath
    )
    return (
      getIcon(icon, destinationIcon) || (
        <DestinationMarkerIcon sx={destinationIcon} />
      )
    )
  }, [theme, destinationIcon, destinationCategoryIconPath])

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const handleCloseDrawer = useCallback(() => {
    setOpen(false)
  }, [])

  const handleChange = useCallback(
    ({ origin, destination }) => {
      const originParam = origin?.id || "unknown"
      const destinationParam = destination?.id || "unknown"
      navigate(`/maps/dir/${originParam}/${destinationParam}`, {
        replace: true,
      })
    },
    [navigate]
  )

  const handleClickStep = useCallback(() => setOpen((prev) => !prev), [])

  return (
    <>
      <Backdrop open={isCalculatingRoute}>
        <CircularProgress />
      </Backdrop>
      <Box sx={root}>
        <Box sx={headerWrapper}>
          {open ? (
            <Fab variant="rounded" sx={backButton} onClick={handleCloseDrawer}>
              {backIcon}
            </Fab>
          ) : (
            <DirectionSearchContainer
              origin={originData}
              destination={destinationData}
              onChange={handleChange}
              onBack={handleClickBack}
              userLocation={userLocation}
              lastScannedLocation={_.last(scannedLocations)}
            />
          )}
        </Box>
        {stepsAvailable && (
          <>
            {/* Direction Overview */}
            <Slide
              direction="up"
              in={stepsAvailable && !open}
              mountOnEnter
              unmountOnExit
            >
              <Box sx={container}>
                <Box sx={actionButtonWrapper}>
                  <Paper elevation={1} sx={paper}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={elevatorPreferredMode}
                          onChange={toggleElevatorPreference}
                        />
                      }
                      label={t("Avoid Steps")}
                      componentsProps={{ typography: { variant: "body2" } }}
                      sx={formControlLabel}
                    />
                  </Paper>
                  {isMultiOrdinalVenue && (
                    <OrdinalSelector
                      levels={relatedLevelWithSteps}
                      onClickOrdinal={changeOrdinal}
                      selectedOrdinal={currentOrdinal}
                    />
                  )}
                </Box>

                <Paper sx={cardContainer}>
                  <Box sx={cardBody}>
                    <Box sx={locationHeaderWrapper}>
                      <Box sx={locationContainer}>
                        <Typography textAlign="start" sx={secondaryText}>
                          {t("Origin")}
                        </Typography>
                        <Typography
                          textAlign="start"
                          variant="h4"
                          sx={primaryText}
                        >
                          {t(originName)}
                        </Typography>
                      </Box>

                      <Box sx={locationContainer}>
                        <Typography textAlign="end" sx={secondaryText}>
                          {t("Destination")}
                        </Typography>
                        <Typography
                          textAlign="end"
                          variant="h4"
                          sx={primaryText}
                        >
                          {t(destinationName)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={overviewProgressContainer}>
                      <Box sx={originIconContainer}>
                        <Avatar
                          variant="circular-logo"
                          sx={locationAvatar}
                          src={originLogo}
                        >
                          {getIcon(originCategoryIconPath, null, {
                            imageIconProps: {
                              solidColorFilterId:
                                "directionOverview_avatar_filter",
                            },
                          }) || originIconComp}
                        </Avatar>
                      </Box>
                      <BorderLinearProgress variant="determinate" value={0} />
                      <Box sx={destinationIconContainer}>
                        <Avatar
                          variant="circular-logo"
                          sx={locationAvatar}
                          src={destinationLogo}
                        >
                          {getIcon(destinationCategoryIconPath, null, {
                            imageIconProps: {
                              solidColorFilterId:
                                "directionOverview_avatar_filter",
                            },
                          }) || destinationIconComp}
                        </Avatar>
                      </Box>
                      <Typography variant="caption" sx={durationText}>
                        {t("Travel", {
                          duration,
                          distance,
                        })}
                      </Typography>
                    </Box>
                  </Box>
                  <DirectionCardActions
                    sx={cardAction}
                    stepButtonProps={{ variant: "outlined" }}
                    onStart={startNavigate}
                    onStep={handleClickStep}
                  />
                </Paper>
              </Box>
            </Slide>

            {/* Step Overview */}
            <Slide direction="up" in={open} mountOnEnter unmountOnExit>
              <Box sx={container}>
                <Paper sx={stepCardContainer}>
                  <Box sx={stepCardBody}>
                    <Stepper
                      sx={stepper}
                      orientation="vertical"
                      connector={null}
                    >
                      {originData && (
                        <DirectionStepItem
                          sx={originStep}
                          primaryLabel={t("Origin")}
                          secondaryLabel={t("Location Floor", {
                            location: t(originName),
                            level: originData.level,
                          })}
                          stepLabelProps={{
                            icon: (
                              <Box sx={stepIconContainer}>
                                <Avatar
                                  variant="circular-logo"
                                  sx={stepAvatar}
                                  src={originLogo}
                                >
                                  {getIcon(originCategoryIconPath, null, {
                                    imageIconProps: {
                                      solidColorFilterId:
                                        "stepOverview_avatar_filter",
                                    },
                                  }) || originIconComp}
                                </Avatar>
                              </Box>
                            ),
                          }}
                        />
                      )}
                      {steps.map((step, index) => {
                        const { description } = step
                        return (
                          <DirectionStepItem
                            index={index}
                            navigateTo={`navigate/steps/${index + 1}`}
                            primaryLabel={
                              <Trans
                                components={{
                                  category: highlightedStepText,
                                  destination: highlightedStepText,
                                  landmark: highlightedStepText,
                                }}
                              >
                                {t(description.text, {
                                  category: t(description.options?.category),
                                  destination: t(
                                    description.options?.destination
                                  ),
                                  landmark: t(description.options?.landmark),
                                })}
                              </Trans>
                            }
                            stepLabelProps={{
                              StepIconComponent: DirectionStepIcon,
                            }}
                            key={`step-description-${index}`}
                          />
                        )
                      })}
                      {destinationData && (
                        <DirectionStepItem
                          sx={destinationStep}
                          primaryLabel={t("Destination")}
                          secondaryLabel={t("Location Floor", {
                            location: t(destinationName),
                            level: destinationData.level,
                          })}
                          stepLabelProps={{
                            icon: (
                              <Box sx={stepIconContainer}>
                                <Avatar
                                  variant="circular-logo"
                                  sx={stepAvatar}
                                  src={destinationLogo}
                                >
                                  {getIcon(destinationCategoryIconPath, null, {
                                    imageIconProps: {
                                      solidColorFilterId:
                                        "stepOverview_avatar_filter",
                                    },
                                  }) || destinationIconComp}
                                </Avatar>
                              </Box>
                            ),
                          }}
                        />
                      )}
                    </Stepper>
                  </Box>
                </Paper>
              </Box>
            </Slide>
          </>
        )}
      </Box>
    </>
  )
}
